// framework imports - 1st party
import React, { useState, useRef, useEffect } from "react";
import withRouter from "../HelperComponents/WithRouter";

// lib imports - 3rd party
import moment from "moment";

// app imports
import { ReactComponent as Approve } from "../../icons/approve-off.svg";
import { ReactComponent as Send } from "../../icons/send-off.svg";
import { ReactComponent as Note } from "../../icons/note-off.svg";
import Resend from "../HelperComponents/Resend";
import * as Auth from "../../authService";
import * as api from "../../api";
import * as notify from "../../notify";
import "../../stylesheets/Row.css";
import "../../stylesheets/GenerateReport.css";
import { getSummaryCheckboxPresentation } from "./transactionCheckboxState";

const LEGACY_SELECTABLE_CHECKBOX_STATUSES = ["READY", "APPROVED"];

// a given row on the transactions summary page
const Row = (props) => {
  // if row is checked
  const [showModal, setShowModal] = useState(false);
  const useTransactionSummaryCheckboxRules =
    props.transactionSummaryCheckboxRules === true;

  //Fields to make the call to update Notify table
  let type = "";
  let destination = "";
  let attention = "";

  // Get Notify Info for Notification request
  const getNotifyInfo = () => {
    api
      .getResendDropdown(props.transaction.transactionId)
      .then((x) => {
        if (x.status === 200 || x.status === 201) {
          //Setting the values from Notify Info to update Notify table
          const wireEntry = x.data.find((item) => item.value === "wir");
          if (wireEntry) {
            type = wireEntry.value;
            destination = wireEntry.notifyDest;
            attention = wireEntry.notifyAttn;
            handleWireNotify();
            return;
          }
        }
        props.retrieveInfo();
      })
      .catch(() => {
        props.retrieveInfo();
      });
  };

  const handleWireNotify = () => {
    api
      .sendNotification(
        props.transaction.transactionId,
        type,
        destination,
        attention
      )
      .then((x) => {
        if (x.status === 200 || x.status === 201) {
          props.retrieveInfo();
        } else {
          notify.error(
            "Notification Failed for reference #" +
              props.transaction.transactionId
          );
          props.retrieveInfo();
        }
      });
  };

  const lowercase = (s) => {
    if (typeof s !== "string") return "";
    return s
      .split(" ")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase() + " ");
  };

  const isEscheated = () =>
    props.transaction.escheated === 1 || props.transaction.escheated === true;

  // determine if payout date exists, and if it's estimated or not based on status
  const renderPayout = (status) => {
    if (isEscheated()) {
      return "-";
    }
    switch (status) {
      case "APPROVED":
        return moment(props.transaction.estimatedPaidDate).format("MM/DD/YYYY");
      case "PAID":
        return moment(props.transaction.paidTime).format("MM/DD/YYYY");
      default:
        return "-";
    }
  };

  // determines if transaction status is denied and changes wording to rejected as per business request

  const renderStatus = (status) => {
    if (isEscheated()) {
      return "ESCHEATED";
    }
    if (props.transaction.confirmationStatus === "DENIED") {
      return "REJECTED";
    }
    if (props.transaction.confirmationStatus === "ECHECKPENDING") {
      return "ECHECK PENDING";
    }
    if (props.transaction.confirmationStatus === "CASHPENDING") {
      return "CASH PENDING";
    } else {
      return props.transaction.confirmationStatus;
    }
  };

  // on click, go to transaction details
  const loadTransaction = () => {
    if (!props.loading) {
      if (props.params.id) {
        props.navigate(
          "/recurring/" +
            props.params.id +
            "/transaction/" +
            props.transaction.transactionId,
          {
            state: {
              transactionId: props.transaction.transactionId,
              prevScreen: "/recurring/" + props.params.id + "/transaction",
              writeNote: false,
            },
          }
        );
      } else {
        props.navigate("/transaction/" + props.transaction.transactionId, {
          state: {
            transactionId: props.transaction.transactionId,
            recurringPaymentId: props.transaction.recurringPaymentId,
            prevScreen: "/summary",
            writeNote: false,
          },
        });
      }
    }
  };

  // when clicking the "note" icon, go to the bottom of the page
  const handleNote = () => {
    if (!props.loading) {
      if (props.params.id) {
        props.navigate(
          "/recurring/" +
            props.params.id +
            "/transaction/" +
            props.transaction.transactionId,
          {
            state: {
              transactionId: props.transaction.transactionId,
              prevScreen: "/recurring/" + props.params.id + "/transaction",
              writeNote: true,
            },
          }
        );
      } else {
        props.navigate("/transaction/" + props.transaction.transactionId, {
          state: {
            transactionId: props.transaction.transactionId,
            recurringPaymentId: props.transaction.recurringPaymentId,
            prevScreen: "/summary",
            writeNote: true,
          },
        });
      }
    }
  };

  const approveRow = () => {
    if (Auth.isEscheatedReadOnly(isEscheated())) {
      return;
    }
    if (props.transaction.confirmationStatus === "READY") {
      api.approveTransactions([props.transaction.transactionId]).then((x) => {
        if (x.status === 200 || x.status === 201) {
          notify.success(
            "Approved transaction " + props.transaction.transactionId
          );
          getNotifyInfo();
        } else {
          notify.error(
            "Failed to approve transaction " + props.transaction.transactionId
          );
        }
      });
    }
  };
  // Whether or not row is checked is determined by the state
  const isChecked = (e) => {
    if (props.rowsChecked) {
      return props.rowsChecked.includes(e);
    }
    return false;
  };

  // handle adding checkmark
  // since a checkmark must change the state
  // of the table component
  const addChecked = (e) => {
    if (!isChecked(e)) {
      const newRows = [...props.rowsChecked, e];
      props.handleRowsChecked(newRows);
      props.handleTopChecked(newRows);
      return true;
    } else {
      return false;
    }
  };

  // handle removing checkmark
  // since a checkmark must change the state
  // of the table component
  const removeChecked = (e) => {
    if (isChecked(e)) {
      const newRows = props.rowsChecked.filter((id) => id !== e);
      props.handleRowsChecked(newRows);
      props.handleTopChecked(newRows);
      return true;
    } else {
      return false;
    }
  };

  const isLegacyCheckboxDisabled = () => {
    if (isEscheated()) {
      return true;
    }
    const status = props.transaction.confirmationStatus || "";
    return !LEGACY_SELECTABLE_CHECKBOX_STATUSES.includes(status.toUpperCase());
  };

  const approvedSelectable = Auth.canEditApprovedSummaryCheckbox();
  const checkboxPresentation = useTransactionSummaryCheckboxRules
    ? getSummaryCheckboxPresentation(
        props.transaction.confirmationStatus,
        isChecked(props.referenceId),
        isEscheated(),
        approvedSelectable
      )
    : null;

  const transactionSummaryCheckboxRef = useRef(null);

  useEffect(() => {
    const input = transactionSummaryCheckboxRef.current;
    if (
      !input ||
      !useTransactionSummaryCheckboxRules ||
      !checkboxPresentation
    ) {
      return;
    }
    input.indeterminate = checkboxPresentation.indeterminate === true;
  }, [
    useTransactionSummaryCheckboxRules,
    checkboxPresentation,
    props.transaction.confirmationStatus,
    props.rowsChecked,
  ]);

  const handleChecked = (e) => {
    if (useTransactionSummaryCheckboxRules) {
      if (!checkboxPresentation.selectable) {
        return;
      }
    } else if (isLegacyCheckboxDisabled()) {
      return;
    }
    e.target.checked
      ? addChecked(props.referenceId)
      : removeChecked(props.referenceId);
  };

  const renderCheckbox = () => {
    if (useTransactionSummaryCheckboxRules) {
      return (
        <input
          ref={transactionSummaryCheckboxRef}
          aria-label={
            checkboxPresentation.indeterminate
              ? "Transaction not selectable"
              : "summary-checkbox"
          }
          type="checkbox"
          checked={checkboxPresentation.checked}
          disabled={checkboxPresentation.disabled}
          onChange={handleChecked}
          onClick={(event) => event.stopPropagation()}
        />
      );
    }

    return (
      <input
        aria-label="summary-checkbox"
        type="checkbox"
        checked={!isLegacyCheckboxDisabled() && isChecked(props.referenceId)}
        disabled={isLegacyCheckboxDisabled()}
        onChange={handleChecked}
      />
    );
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleRowClick = () => {
    if (window.innerWidth < 600) {
      loadTransaction();
    }
  };

  //checks if user have privileges to resend notes
  const showIfUserHasPrivilege = () => {
    return (
      !Auth.isEscheatedReadOnly(isEscheated()) &&
      (Auth.canFinanceOverride() ||
        Auth.canAdmin() ||
        Auth.canAgencyEdit() ||
        Auth.canAgencyAdmin())
    );
  };

  const { paymentType, paymentAuthInfoList, displayDefendantName } =
    props.transaction;
  let displayName = "";
  if (paymentAuthInfoList.length > 0 && displayDefendantName === "") {
    const { payNearMeInfo, creditCardInfo, echeckInfo, greenDotInfo } =
      paymentAuthInfoList[0];
    if (paymentType === "CREDIT_CARD" && creditCardInfo != null) {
      displayName = creditCardInfo.lastName + ", " + creditCardInfo.firstName;
    } else if (paymentType === "PAYNEARME" && payNearMeInfo != null) {
      displayName = payNearMeInfo.lastName + ", " + payNearMeInfo.firstName;
    } else if (paymentType === "GREENDOT" && greenDotInfo != null) {
      displayName = greenDotInfo.lastName + ", " + greenDotInfo.firstName;
    } else if (paymentType === "ECHECK" && echeckInfo != null) {
      displayName = echeckInfo.lastName + ", " + echeckInfo.firstName;
    }
  } else {
    displayName = displayDefendantName;
  }

  return (
    <tr className="row" onClick={handleRowClick} name={props.referenceId}>
      <td className="hide-mobile">{renderCheckbox()}</td>
      <td className="transaction-id-row mobile-left" onClick={loadTransaction}>
        <button className="transaction-id-button">
          {props.transaction.cartId
            ? props.transaction.transactionId + "**"
            : props.transaction.recurringPaymentId &&
              !props.isRedirectFromRecurring
            ? props.transaction.transactionId + "*"
            : props.transaction.transactionId}
        </button>
      </td>
      <td className="text-right hide-mobile" onClick={loadTransaction}>
        {props.transaction.plcId}
      </td>
      {Auth.canViewSource() ? (
        <td className="text-right hide-mobile" onClick={loadTransaction}>
          {props.transaction.application}
        </td>
      ) : null}
      <td className="text-right" onClick={loadTransaction}>
        {props.transaction.startTime
          ? moment(props.transaction.startTime).format("M/DD/YYYY hh:mm A")
          : null}
      </td>
      <td className="name-row mobile-left" onClick={loadTransaction}>
        {displayName.replace("null", "")}
      </td>
      <td
        className={
          "text-right hide-mobile " + props.transaction.confirmationStatus
        }
        onClick={loadTransaction}
      >
        {renderPayout(props.transaction.confirmationStatus)}
      </td>
      <td className="hide-mobile" onClick={loadTransaction}>
        {lowercase(renderStatus(props.transaction.confirmationStatus))}
      </td>
      <td className="payment-row text-right" onClick={loadTransaction}>
        {props.transaction.displayTotalAmount
          ? props.transaction.displayTotalAmount
          : "$0.00"}
      </td>
      <td className="hide-mobile">
        {!Auth.isEscheatedReadOnly(isEscheated()) &&
        (Auth.canAdmin() ||
          Auth.canFinanceOverride() ||
          Auth.canAmountModify() ||
          Auth.canModify()) ? (
          <div className="row-button" onClick={approveRow}>
            <Approve className="row-icon" />
            <div className="row-description">Approve</div>
          </div>
        ) : null}
        {showIfUserHasPrivilege() ? (
          <div className="row-button" onClick={handleOpenModal}>
            <Send className="row-icon" />
            <div className="row-description">Send</div>
          </div>
        ) : null}
        <div className="row-button" onClick={handleNote}>
          <Note className="row-icon" />
          <div className="row-description">Write Note</div>
        </div>
      </td>
      <Resend
        showModal={showModal}
        transactionId={props.transaction.transactionId}
        setShowModal={(e) => setShowModal(e)}
      />
    </tr>
  );
};

export default withRouter(Row);
